import * as React from "react";
import * as _ from "underscore";
import { connect } from "react-redux";
import { Button } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";

import { AppShape, HurdleShape } from "@eave/shared/types/api";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import APIClient from "../../APIClient";
import { HurdleStatus } from "../HurdleStatus";

import { createHurdleOverride, updateHurdleOverride, deleteHurdleOverride } from "../actions";

require(`../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/\
owner_occupancy.scss`);

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");
const classnames = require("classnames");

const EMDASH = <span className="owner-occupancy__emdash">—</span>;

interface EditorProps {
  dispatch?: Function;
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

class Editor extends React.Component<EditorProps, any> {
  _modal = null;

  constructor(props) {
    super(props);

    const { override } = props.hurdle;

    const expl = override && override.values && override.values.explanation;

    this.state = {
      purchaseContexts: null,
      expanded: [],
      explanation: expl || null,
    };
  }

  componentDidMount() {
    const { app } = this.props;

    // TODO: jjw: move state to redux

    APIClient.get(
      `/loan_applications/${app.id}/hurdles/owner_occupancy/purchase_contexts`,
      resp => {
        this.setState({
          ...this.state,
          purchaseContexts: resp.purchase_contexts,
        });
      },
    );
  }

  componentWillReceiveProps(nextProps) {
    const { app, hurdle } = nextProps;

    if (app.id === this.props.app.id) return;

    const expl = hurdle.override && hurdle.override.values && hurdle.override.values.explanation;

    APIClient.get(
      `/loan_applications/${app.id}/hurdles/owner_occupancy/purchase_contexts`,
      resp => {
        this.setState({
          ...this.state,
          purchaseContexts: resp.purchase_contexts,
          explanation: expl || null,
        });
      },
    );
  }

  explanationHasChanged() {
    const { override } = this.props.hurdle;
    const { explanation } = this.state;

    return (
      ((!override || !override.values) && explanation) ||
      (override && override.values && override.values.explanation !== explanation)
    );
  }

  onSave() {
    const { app, hurdle, onHide, dispatch } = this.props;
    const { explanation } = this.state;

    const { override } = hurdle;

    if (!override && explanation) {
      createHurdleOverride(dispatch, {
        appId: app.id,
        hurdleName: hurdle.name,
        hurdleOverrideValues: { explanation },
      });
    } else if (override && !explanation) {
      deleteHurdleOverride(dispatch, {
        appId: app.id,
        hurdleOverrideId: override.id,
      });
    } else if (override && this.explanationHasChanged()) {
      updateHurdleOverride(dispatch, {
        appId: app.id,
        hurdleOverrideId: override.id,
        hurdleOverrideValues: { explanation },
      });
    }

    onHide();
  }

  toggleExpanded(ctxName) {
    const { expanded } = this.state;

    let update;
    if (_.contains(expanded, ctxName)) {
      update = _.without(expanded, ctxName);
    } else {
      update = _.union(expanded, [ctxName]);
    }

    this.setState({
      ...this.state,
      expanded: update,
    });
  }

  deleteExpl() {
    if (confirm("Are you sure you want to delete this override?")) {
      this.setState({
        ...this.state,
        explanation: null,
      });
    }
  }

  upsertExpl() {
    const { explanation } = this.state;

    let expl;
    if (explanation) {
      expl = prompt("Override with explanation", explanation);
    } else {
      expl = prompt("Override with explanation");
    }

    if (expl !== null) {
      this.setState({
        ...this.state,
        explanation: expl.length ? expl : null,
      });
    }
  }

  renderStatusWithNote() {
    const { status } = this.props.hurdle;
    const { purchaseContexts, explanation } = this.state;

    // logic here ensures status looks correct if explanation modified but
    // not yet synced to backend to yield new status (based on explanation)

    let displayStatus;
    if (explanation) {
      displayStatus = "green";
    } else if (status === "red") {
      displayStatus = "red";
    } else if (_.any(_.pluck(purchaseContexts, "applies"))) {
      displayStatus = "green";
    } else {
      displayStatus = "yellow";
    }

    let note;
    if (explanation) {
      note = `Overridden with explanation: ${explanation}`;
    } else if (displayStatus === "red") {
      note = "Not enough info yet to evaluate purchase contexts";
    } else if (displayStatus === "yellow") {
      note = "No valid purchase contexts apply";
    } else if (displayStatus === "green") {
      note = "At least one valid purchase context applies";
    } else {
      throw new Error("not reached");
    }

    return (
      <div className="owner-occupancy__status">
        <HurdleStatus status={displayStatus} />
        <span style={{ marginLeft: "0.75rem" }}>{note}</span>
        {explanation && (
          <span
            style={{ marginLeft: "0.75rem" }}
            className="owner-occupancy__clickable"
            onClick={this.deleteExpl.bind(this)}
          >
            <i className="fa fa-close" />
          </span>
        )}
        {(explanation || displayStatus === "yellow" || displayStatus === "red") && (
          <span
            style={{ marginLeft: "0.75rem" }}
            className="owner-occupancy__clickable"
            onClick={this.upsertExpl.bind(this)}
          >
            <i className="fa fa-pencil" />
          </span>
        )}
      </div>
    );
  }

  renderTable() {
    const { purchaseContexts } = this.state;

    return (
      <table className="table table-striped table-hover">
        <thead>
          <tr>
            <th className="col-sm-1" />
            <th className="col-sm-1">Applies</th>
            <th>Purchase context</th>
          </tr>
        </thead>
        <tbody>
          {purchaseContexts.map(ctx => {
            const isExpanded = _.contains(this.state.expanded, ctx.name);

            const expandedClasses = classnames({
              fa: true,
              "fa-plus-square-o": !isExpanded,
              "fa-minus-square-o": isExpanded,
            });

            let icon;
            if (ctx.sufficient_info === false) {
              icon = (
                <div className="owner-occupancy__insufficient-info">
                  <i className="fa fa-question" />
                </div>
              );
            } else if (ctx.applies) {
              icon = (
                <div className="owner-occupancy__applies">
                  <i className="fa fa-check" />
                </div>
              );
            } else {
              icon = EMDASH;
            }

            return (
              <tr key={ctx.name} className="owner-occupancy__row">
                <td
                  className="owner-occupancy__clickable text-center"
                  onClick={this.toggleExpanded.bind(this, ctx.name)}
                >
                  <i className={expandedClasses} />
                </td>
                <td className="text-center">{icon}</td>
                <td>
                  <div className={isExpanded && "owner-occupancy__name--expanded"}>{ctx.name}</div>
                  {isExpanded && <div className="owner-occupancy__desc">{ctx.explanation}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  setRef(ref) {
    if (ref) {
      this._modal = ref;
    }
  }

  render() {
    const { app, hurdle, onHide } = this.props;

    let content;
    if (this.state.purchaseContexts) {
      content = (
        <div>
          {this.renderStatusWithNote()}
          {this.renderTable()}
        </div>
      );
    } else {
      content = <p>Loading…</p>;
    }

    let footer;
    if (this.explanationHasChanged()) {
      footer = (
        <footer className={styles.footer}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button kind="primary" onClick={this.onSave.bind(this)} renderIcon={Save16}>
            Save changes
          </Button>
        </footer>
      );
    } else {
      footer = (
        <footer className={styles.footer}>
          <Button bsSize="small" onClick={onHide}>
            <i className="fa fa-thumbs-up" /> OK
          </Button>
        </footer>
      );
    }

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        {content}
        {footer}
      </HurdleView>
    );
  }
}

export default connect()(Editor);
